<script setup>
/**
 * TiptapToolbar
 * Layout orchestrator for the rich-text editor (Tiptap) control suite.
 * Manages groupings for text formatting adjustments, list toggles, link manipulation
 * (including system-specific PID/DID structures), media insertion, layout alignment,
 * and presentation settings like full-screen and raw-source code modes.
 */
import {computed} from 'vue'
import {useUserStore} from '@/stores/user'

// Component Imports
import TiptapToolbarBtn from "./TiptapToolbarBtn.vue"
import TiptapToolbarStylesMenu from "./TiptapToolbarStylesMenu.vue"
import TiptapToolbarBlockMenu from "./TiptapToolbarBlockMenu.vue"
import TiptapToolbarDocumentLink from "./TiptapToolbarDocumentLink.vue"
import TiptapToolbarPageLink from "./TiptapToolbarPageLink.vue"
import TiptapToolbarImage from "./TiptapToolbarImage.vue"

const props = defineProps({
    editor: {
        type: Object,
        required: true,
    },
    isFullScreen: {
        type: Boolean,
        required: true,
    },
    isSourceMode: {
        type: Boolean,
        required: true,
    },
    pasteAsPlainTextActive: {
        type: Boolean,
        required: true,
    }
})

// Pinia Store
const userStore = useUserStore()

const emit = defineEmits(['open-image-modal', 'toggle-full-screen', 'toggle-source'])

// --- Computed ---

const isPidLink = computed(() => {
    const attrs = props.editor.getAttributes('link')
    return attrs?.href ? /^\[PID:\d+\]$/.test(attrs.href) : false
})

const isDidLink = computed(() => {
    const attrs = props.editor.getAttributes('link')
    return attrs?.href ? /^\[DID:\d+\]$/.test(attrs.href) : false
})

const isMac = computed(() => {
    // navigator.userAgentData is preferred in modern browsers, fallback to navigator.platform
    const platform = navigator.userAgentData?.platform || navigator.platform || ''
    return /Mac|iPhone|iPod|iPad/.test(platform)
})

const isDarkMode = computed(() => userStore.pref('darkMode'))

const modKey = computed(() => isMac.value ? '⌘' : 'Ctrl')
const shiftKey = computed(() => isMac.value ? '⇧' : 'Shift')

// --- Methods ---

const setLink = () => {
    const previousUrl = props.editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    if (url === null) return

    if (url === '') {
        props.editor.commands.unsetLink()
        return
    }

    // Highlight/select the entire existing link first, then apply the new URL
    props.editor
        .chain()
        .focus()
        .extendMarkRange('link') // <-- This is the magic ingredient
        .setLink({href: url})
        .run()
}

const setTextAlign = (alignment) => {
    if (props.editor.isActive({textAlign: alignment})) {
        props.editor.chain().focus().unsetTextAlign().run()
    } else {
        props.editor.chain().focus().setTextAlign(alignment).run()
    }
}

const toggleFullScreen = () => {
    emit('toggle-full-screen')
}

const toggleSourceMode = () => {
    emit('toggle-source')
}
</script>

<template>
    <div @mousedown.prevent v-if="editor"
         class="tiptap-toolbar d-flex flex-wrap gap-1 align-items-center p-1 border-bottom rounded rounded-bottom-0"
         :class="{'bg-light': !isDarkMode}"
         role="toolbar" aria-label="Tiptap editor toolbar">

        <div class="btn-group btn-group-sm pe-1 border-end rounded-0" role="group">
            <TiptapToolbarBtn :iconClass="'bi-type-bold'" :command="editor.commands.toggleBold"
                              :is-active="editor.isActive('bold')" :title="'Bold (' + modKey + ' B)'"/>
            <TiptapToolbarBtn :iconClass="'bi-type-italic'" :command="editor.commands.toggleItalic"
                              :is-active="editor.isActive('italic')" :title="'Italic (' + modKey + ' I)'"/>
            <TiptapToolbarBtn :iconClass="'bi-type-strikethrough'" :command="editor.commands.toggleStrike"
                              :is-active="editor.isActive('strike')"
                              :title="'Strikethrough (' + modKey + ' ' + shiftKey + ' S)'"/>
            <TiptapToolbarBtn :iconClass="'bi-eraser'" :command="editor.commands.unsetAllMarks"
                              :title="'Remove styling'"/>
        </div>

        <div class="btn-group btn-group-sm pe-1 border-end rounded-0" role="group">
            <TiptapToolbarBtn :iconClass="'bi-text-left'" :command="() => setTextAlign('left')"
                              :is-active="editor.isActive({ textAlign: 'left' })"
                              :title="'Align left (' + modKey + ' ' + shiftKey + ' L)'"/>
            <TiptapToolbarBtn :iconClass="'bi-text-center'" :command="() => setTextAlign('center')"
                              :is-active="editor.isActive({ textAlign: 'center' })"
                              :title="'Align center (' + modKey + ' ' + shiftKey + ' E)'"/>
            <TiptapToolbarBtn :iconClass="'bi-text-right'" :command="() => setTextAlign('right')"
                              :is-active="editor.isActive({ textAlign: 'right' })"
                              :title="'Align right (' + modKey + ' ' + shiftKey + ' R)'"/>
        </div>

        <div class="btn-group btn-group-sm pe-1 border-end rounded-0" role="group">
            <TiptapToolbarImage :editor="editor" @open-image-modal="$emit('open-image-modal')" :title="'Image'"/>
            <TiptapToolbarBtn :iconClass="'bi-hr'" :command="editor.commands.setHorizontalRule"
                              :title="'Horizontal rule (---)'"/>
            <TiptapToolbarBtn :iconClass="'bi-list-ol'" :command="editor.commands.toggleOrderedList"
                              :is-active="editor.isActive('orderedList')"
                              :title="'Ordered list (' + modKey + ' ' + shiftKey + ' 7)'"/>
            <TiptapToolbarBtn :iconClass="'bi-list-ul'" :command="editor.commands.toggleBulletList"
                              :is-active="editor.isActive('bulletList')"
                              :title="'Bulleted list (' + modKey + ' ' + shiftKey + ' 8)'"/>
            <TiptapToolbarBtn :iconClass="'bi-blockquote-left'" :command="editor.commands.toggleBlockquote"
                              :is-active="editor.isActive('blockquote')" :title="'Blockquote (>)'"/>
        </div>

        <div class="btn-group btn-group-sm pe-1 border-end rounded-0" role="group">
            <TiptapToolbarBtn :iconClass="'bi-link'" :command="setLink"
                              :is-active="editor.isActive('link') && !isPidLink && !isDidLink"
                              :title="'Link (' + modKey + ' K)'"/>
            <TiptapToolbarPageLink :editor="editor" :is-pid-link="isPidLink" :title="'Page link'"/>
            <TiptapToolbarDocumentLink :editor="editor" :is-did-link="isDidLink" :title="'Document link'"/>

            <TiptapToolbarBtn :command="editor.commands.unsetLink" :disabled="!editor.isActive('link')"
                              :title="'Unset link'">
                <span style="position: relative; display: inline-block;">
                        <i class="bi bi-link"></i>
                        <i class="bi bi-slash-lg" style="position: absolute; left: 0; top: 0"></i>
                    </span>
            </TiptapToolbarBtn>
            <!--<button type="button" class="btn btn-light" @click="editor.commands.unsetLink()"
                    :disabled="!editor.isActive('link')" :title="'Unset link'">
                    <span style="position: relative; display: inline-block;">
                        <i class="bi bi-link"></i>
                        <i class="bi bi-slash-lg" style="position: absolute; left: 0; top: 0"></i>
                    </span>
            </button>-->
        </div>

        <TiptapToolbarBlockMenu :editor="editor"/>
        <TiptapToolbarStylesMenu :editor="editor"/>

        <div class="btn-group btn-group-sm ps-1 border-start rounded-0" role="group">
            <TiptapToolbarBtn :iconClass="'bi-clipboard-x'"
                              :command="() => { $emit('update:pasteAsPlainTextActive', !pasteAsPlainTextActive) }"
                              :is-active="pasteAsPlainTextActive" :title="'Toggle Paste as Plain Text'"/>
            <TiptapToolbarBtn :iconClass="isFullScreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'"
                              :command="toggleFullScreen" :is-active="isFullScreen" :title="'Full screen'"/>
            <TiptapToolbarBtn :iconClass="'bi-code-slash'" :command="toggleSourceMode" :is-active="isSourceMode"
                              :title="'Source mode'"/>
        </div>
    </div>
</template>